import React from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Building2, Globe, DollarSign, Tag, Upload, ChevronLeft, Menu, Users, LogOut, Clock, FileText } from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';
import { logout, getUser } from '../lib/api';

const adminNavItems = [
  { label: 'Übersicht', path: '/admin', icon: LayoutDashboard, end: true },
  { label: 'Anbieter', path: '/admin/providers', icon: Building2 },
  { label: 'TLDs', path: '/admin/tlds', icon: Globe },
  { label: 'Preise', path: '/admin/prices', icon: DollarSign },
  { label: 'Aktionen', path: '/admin/promotions', icon: Tag },
  { label: 'Import', path: '/admin/import', icon: Upload },
  { label: 'Benutzer', path: '/admin/users', icon: Users },
  { label: 'Rechnungen', path: '/admin/invoices', icon: FileText },
];

export default function AdminShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-shell">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 min-h-screen" data-testid="admin-sidebar">
        <div className="p-4 border-b border-slate-200">
          <NavLink to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
              <Globe className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="font-semibold text-slate-900" style={{fontFamily: 'Space Grotesk'}}>Domain-</span>
              <span className="font-semibold text-sky-600" style={{fontFamily: 'Space Grotesk'}}>Preischeck</span>
            </div>
          </NavLink>
          <p className="text-xs text-muted-foreground mt-1">Admin-Bereich</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {adminNavItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({isActive}) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive ? 'bg-sky-50 text-sky-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-200 space-y-1">
          {user && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              <p className="font-medium text-slate-700">{user.name}</p>
              <p>{user.email}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 rounded-lg hover:bg-red-50 w-full"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4" />
            Abmelden
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-xl">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <span className="font-semibold" style={{fontFamily: 'Space Grotesk'}}>Admin</span>
              <button onClick={() => setSidebarOpen(false)} className="p-1 rounded hover:bg-slate-100">
                <ChevronLeft className="w-5 h-5" />
              </button>
            </div>
            <nav className="p-3 space-y-1">
              {adminNavItems.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  className={({isActive}) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive ? 'bg-sky-50 text-sky-700' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="p-3 border-t border-slate-200">
              <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 w-full rounded-lg hover:bg-red-50">
                <LogOut className="w-4 h-4" /> Abmelden
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200 px-4 sm:px-6 h-14 flex items-center gap-4" data-testid="admin-topbar">
          <button className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
          <h2 className="text-sm font-medium text-slate-600">
            {adminNavItems.find(i => {
              if (i.end) return location.pathname === i.path;
              return location.pathname.startsWith(i.path);
            })?.label || 'Admin'}
          </h2>
        </header>
        <main className="flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
